// components/account/AccountHistoricsModal/AccountHistoricsModal.jsx
import { useState } from 'react';
import { Modal, Tabs, Tab, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { 
  FaFileInvoiceDollar,
  FaHistory,
  FaFileAlt,
  FaChartBar,
  FaDatabase
} from 'react-icons/fa';

// Composants internes
import StatisticsPanel from './components/StatisticsPanel';
import FiltersPanel from './components/FiltersPanel';
import HistoricsTable from './components/HistoricsTable';
import DocumentsPanel from './components/DocumentsPanel';
import CategorySummary from './components/CategorySummary';
import { useAccountHistorics } from '../service/Hooks/AccountHistorics/useAccountHistorics';
import DocumentViewerModal from './components/Modal/DocumentViewerModal';

// Utilitaires

/**
 * Modal d'historique complet d'un compte
 * Affiche toutes les informations: statistiques, tableaux, documents, etc.
 */
const AccountHistoricsModal = ({ 
  show, 
  onHide, 
  account, 
  domainId 
}) => {
  // État pour le modal de visualisation de document
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Hook personnalisé pour gérer les données
  const {
    loading,
    error,
    data,
    filteredDocuments,
    filteredHistorics,
    filters,
    updateFilter,
    resetFilters,
    refetch
  } = useAccountHistorics(account, domainId, show);

  // Ajouter un état pour la taille de page
  const [tablePageSize, setTablePageSize] = useState(15);
  /**
   * Gère l'ouverture d'un document
   */
  const handleOpenDocument = (documentOrHistoric) => {
    if (documentOrHistoric.url) {
      // C'est un document avec URL
      setSelectedDocument(documentOrHistoric);
      setShowDocumentViewer(true);
    } else if (documentOrHistoric.hasDocument) {
      // C'est un historic qui a un document, on pourrait implémenter
      // une recherche du document correspondant
      console.log('Ouvrir document pour historic:', documentOrHistoric.id);
    }
  };

  /**
   * Gère le téléchargement d'un document
   */
  const handleDownloadDocument = async (document) => {
    if (!document.url) return;

    try {
      const response = await fetch(document.url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = document.fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      alert('Erreur lors du téléchargement du document');
    }
  };

  /**
   * Affiche le contenu principal en fonction de l'état
   */
  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3">Chargement des données d'historique...</p>
          <small className="text-muted">
            Récupération des mouvements et documents
          </small>
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="danger">
          <div className="d-flex">
            <div className="flex-grow-1">
              <h5>Erreur de chargement</h5>
              <p className="mb-0">{error}</p>
            </div>
            <button 
              className="btn btn-outline-danger btn-sm align-self-start"
              onClick={refetch}
            >
              Réessayer
            </button>
          </div>
        </Alert>
      );
    }

    if (!data) {
      return (
        <Alert variant="warning">
          <h5>Aucune donnée disponible</h5>
          <p className="mb-0">
            Aucun historique trouvé pour ce compte.
          </p>
        </Alert>
      );
    }

    return (
      <>
        {/* Statistiques en haut */}
        <StatisticsPanel data={data} />

        {/* Panel de filtres */}
        <FiltersPanel
          filters={filters}
          onFilterChange={updateFilter}
          onResetFilters={resetFilters}
          categories={data.summary?.categories || []}
        />

        {/* Onglets pour les différentes vues */}
        <Tabs defaultActiveKey="historics" className="mb-4">
          {/* Onglet: Tous les historiques */}
          <Tab 
            eventKey="historics" 
            title={
              <>
                <FaHistory className="me-2" />
                Tous les historiques
                <span className="badge bg-secondary bg-opacity-10 border-primary text-primary ms-2">
                  {filteredHistorics.length}
                </span>
              </>
            }
          >
            <div className="mt-3">
              <HistoricsTable 
                historics={filteredHistorics}
                onViewDocument={handleOpenDocument}
                pageSize={tablePageSize}
              />
            </div>
          </Tab>

          {/* Onglet: Documents uniquement */}
          <Tab 
            eventKey="documents" 
            title={
              <>
                <FaFileAlt className="me-2" />
                Documents
                <span className="badge  bg-opacity-10 border-primary text-primary ms-2">
                  {filteredDocuments.length}
                </span>
              </>
            }
          >
            <div className="mt-3">
              <DocumentsPanel
                documentsByContainer={data.documentsByContainer || []}
                onOpenDocument={handleOpenDocument}
                onDownloadDocument={handleDownloadDocument}
              />
            </div>
          </Tab>

          {/* Onglet: Analyse */}
          <Tab 
            eventKey="analysis" 
            title={
              <>
                <FaChartBar className="me-2" />
                Analyse
              </>
            }
          >
            <div className="mt-3">
              <Row>
                {/* Résumé par catégorie */}
                <Col md={8}>
                  <CategorySummary
                    categories={data.summary?.categories || []}
                    totalAmount={data.summary?.totalAmount || 0}
                  />
                </Col>
                
                {/* Informations techniques */}
                <Col md={4}>
                  <div className="card border h-100">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <FaDatabase className="me-2" />
                        Informations techniques
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="mb-3">
                        <small className="text-muted d-block">Date de début</small>
                        <strong>{data.summary?.dateRange?.split(' à ')[0] || 'N/A'}</strong>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Date de fin</small>
                        <strong>{data.summary?.dateRange?.split(' à ')[1] || 'N/A'}</strong>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Documents groupés</small>
                        <strong>{data.documentsByContainer?.length || 0} containers</strong>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted d-block">Taux de succès</small>
                        <strong>{data.containerStatistics?.successRate || 0}%</strong>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Tab>
        </Tabs>
      </>
    );
  };

  return (
    <>
      {/* Modal principal */}
      <Modal 
        show={show} 
        onHide={onHide}
        size="custom"
        centered
        scrollable
        className="account-historics-modal"
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title>
            <div className="d-flex align-items-center">
              <FaFileInvoiceDollar className="me-3 text-primary" size={24} />
              <div>
                <h5 className="mb-0">Historique Complet</h5>
                <small className="text-muted">
                  {account?.displayName || account?.companyName || 'Compte'}
                  {/* {domainId && ` • Domaine: ${domainId}`} */}
                </small>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {renderContent()}
        </Modal.Body>
        
        <Modal.Footer className="bg-light">
          <div className="d-flex justify-content-between w-100">
            <div>
              <small className="text-muted">
                Données actualisées automatiquement
              </small>
            </div>
            <div className="d-flex gap-2">
              <button 
                className="filter-button"
                onClick={refetch}
                disabled={loading}
              >
                Actualiser
              </button>
              <button 
                className="filter-button" 
                onClick={onHide}
              >
                Fermer
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal de visualisation de document */}
      {showDocumentViewer && selectedDocument && (
        <DocumentViewerModal
          show={showDocumentViewer}
          onHide={() => {
            setShowDocumentViewer(false);
            setSelectedDocument(null);
          }}
          historicId={selectedDocument.id}
          domainId={domainId}
          accountHistoric={selectedDocument}
        />
      )}
    </>
  );
};

export default AccountHistoricsModal;